import { TranslocoModule, TranslocoService, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Location } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HeaderService } from 'core';
import { CustomerConfigurationService, CustomerConfigurationState } from '../../services/customer-configuration.service';

@Component({
  selector: 'ord-configure-customer-zones',
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatFormFieldModule, MatSelectModule, RouterLink, TranslocoModule],
  providers: [provideTranslocoScope('orders')],
  templateUrl: './configure-customer-zones.html',
  styleUrl: './configure-customer-zones.scss',
})
export class ConfigureCustomerZones implements OnInit, OnDestroy {
  readonly configuration = inject(CustomerConfigurationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly headerService = inject(HeaderService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly proposalId = this.route.snapshot.queryParamMap.get('id');
  readonly customer = computed(() => this.configuration.proposalId() === this.proposalId ? this.configuration.customer() : null);
  readonly form = new FormGroup({
    oil: new FormControl<string | null>(null),
    nonOil: new FormControl<string | null>(null),
  });

  // TODO: Sostituire queste opzioni mock con le zone fornite dal backend.
  // Le descrizioni sono mostrate come arrivano: la traduzione dei dati è gestita dall'API.
  readonly oilZones = [
    { value: 'oil-north', get label() { return translate<string>('orders.ui.oilNord'); } },
    { value: 'oil-centre', get label() { return translate<string>('orders.ui.oilCentro'); } },
    { value: 'oil-south', get label() { return translate<string>('orders.ui.oilSud'); } },
  ];
  readonly nonOilZones = [
    { value: 'non-oil-north', get label() { return translate<string>('orders.ui.nonOilNord'); } },
    { value: 'non-oil-centre', get label() { return translate<string>('orders.ui.nonOilCentro'); } },
    { value: 'non-oil-south', get label() { return translate<string>('orders.ui.nonOilSud'); } },
  ];

  ngOnInit(): void {
    this.headerService.removeActions();
    this.transloco.selectTranslate<string>('newCustomer.title', {}, 'orders')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(title => this.headerService.setTitle(title));

    // Ripristino del contesto anche dopo un refresh della pagina.
    this.configuration.restoreFlowState(this.location.getState() as Partial<CustomerConfigurationState> | null, this.proposalId);

    this.form.setValue({ oil: this.configuration.oilZone(), nonOil: this.configuration.nonOilZone() });
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const value = this.form.getRawValue();
      this.configuration.oilZone.set(value.oil);
      this.configuration.nonOilZone.set(value.nonOil);
    });
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  continue(): void {
    const value = this.form.getRawValue();
    if (!this.customer() || !this.configuration.destinationForConfiguration() || (!value.oil && !value.nonOil)) {
      return;
    }
    this.configuration.oilZone.set(value.oil);
    this.configuration.nonOilZone.set(value.nonOil);
    void this.router.navigate(['/new-order-proposal/configure-customer/contacts'], {
      queryParams: { id: this.proposalId },
      state: this.configuration.getFlowState(),
    });
  }
}
